/**
 * SSE Streaming Client for Admission QnA
 * Uses fetch() with ReadableStream for POST SSE
 * Endpoint: /stream/admission/qna/ask
 */

import ss from '@/Data/Session';
import type { QnaAskParams, QnaSSEEvent } from 'ai-api/types/admission-qna';

// From discovery/260209-admission-chat.json
const STREAM_BASE_URL = 'https://ibz7altr5nr63lvdw4sfwf6s7q0ommmh.lambda-url.ap-northeast-2.on.aws/';
const CHAR_DELAY_MIN = 8;  // ~120 chars/sec
const CHAR_DELAY_MAX = 34; // ~30 chars/sec

/**
 * Callbacks for QnA streaming
 */
export interface QnAStreamCallbacks {
    /** Called when session ID is received (first event) */
    onSession: (sessionid: string) => void;
    /** Called with text delta (append to accumulator) */
    onText: (text: string) => void;
    /** Called with status update (narration-style) */
    onStatus: (content: string) => void;
    /** Called when stream is complete */
    onEnd: () => void;
    /** Called on error */
    onError: (error: Error) => void;
    /** Called when connection closes */
    onClose: () => void;
}

export class QnAStreamClient {
    private abortController: AbortController | null = null;

    /**
     * Stream a QnA question/answer
     */
    async ask(params: QnaAskParams, callbacks: QnAStreamCallbacks): Promise<void> {
        this.disconnect();

        const session = ss.getCurrentSession();
        if (!session?.jwt) {
            callbacks.onError(new Error('Not authenticated'));
            return;
        }

        this.abortController = new AbortController();

        try {
            const response = await fetch(STREAM_BASE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.jwt}`,
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify(params),
                signal: this.abortController.signal,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error((errorData as any).message || `HTTP ${response.status}: ${response.statusText}`);
            }

            if (!response.body) {
                throw new Error('Response body is null');
            }

            await this.processStream(response.body, callbacks);

        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }
            callbacks.onError(error as Error);
        } finally {
            callbacks.onClose();
        }
    }

    private async processStream(body: ReadableStream<Uint8Array>, callbacks: QnAStreamCallbacks): Promise<void> {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
            while (true) {
                const { done, value } = await reader.read();

                if (done) { break; }

                buffer += decoder.decode(value, { stream: true });

                const events = buffer.split('\n\n');
                buffer = events.pop() || '';

                for (const event of events) {
                    if (event.trim()) {
                        const message = this.parseSSEEvent(event);
                        if (message) {
                            await this.handleEvent(message, callbacks);
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock();
        }
    }

    private async handleEvent(event: QnaSSEEvent, callbacks: QnAStreamCallbacks): Promise<void> {
        switch (event.type) {
            case 'session':
                callbacks.onSession(event.sessionid);
                break;

            case 'text': {
                const text = event.content;
                // Split into 1-3 character groups and emit with delays
                let i = 0;
                while (i < text.length) {
                    const groupSize = Math.floor(Math.random() * 3) + 1;
                    const group = text.slice(i, i + groupSize);
                    callbacks.onText(group);
                    i += groupSize;

                    if (i < text.length) {
                        const delay = CHAR_DELAY_MIN + Math.random() * (CHAR_DELAY_MAX - CHAR_DELAY_MIN);
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                }
                break;
            }

            case 'status':
                callbacks.onStatus(event.content);
                break;

            case 'end':
                callbacks.onEnd();
                break;
        }
    }

    private parseSSEEvent(eventText: string): QnaSSEEvent | null {
        const lines = eventText.split('\n');
        let data = '';

        for (const line of lines) {
            if (line.startsWith('data:')) {
                data += line.slice(5).trim();
            }
        }

        if (data) {
            try {
                return JSON.parse(data) as QnaSSEEvent;
            } catch (e) {
                console.error('Failed to parse SSE message:', e, data);
            }
        }

        return null;
    }

    disconnect(): void {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    isConnected(): boolean {
        return this.abortController !== null;
    }
}
